import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router';
import { BrandLockup } from '../components/Brand.tsx';
import { wayRound } from '../publicPages.ts';
import { useMe } from '../session.tsx';

/**
 * The portal's public pages: what it is, its privacy policy and its terms
 * (2026-10-06, for Google's consent screen: the NSB Portal project's Gmail
 * client needs a home page and a privacy policy on the portal's own domain),
 * and the development blog. Anyone can read them, signed in or not; they have
 * no header and no under-development band (Layout, `isPublicPage`).
 *
 * The privacy policy says what the code does. A change to what the portal
 * keeps, or to who else handles it, changes this page in the same commit.
 */

const CONTACT = 'jason@neverstopbuilding.com';
// Each page's own date: the privacy policy changed with Resend sending the portal's emails, the terms did not.
const PRIVACY_EFFECTIVE = 'October 27, 2026';
const TERMS_EFFECTIVE = 'October 6, 2026';
const GOOGLE_POLICY = 'https://developers.google.com/terms/api-services-user-data-policy';

/** The frame every public page shares: the lockup back to sign-in (to the portal, signed in), the title, a line under it, then the page, then the way round. */
export function PublicPage({ title, lede, children }: { title: string; lede: ReactNode; children: ReactNode }) {
  const { data: me } = useMe();
  return (
    // Prose to read, with no header to line up under: one column at the reading width, centred, on the shell's gutters.
    <div className="shell pb-24 pt-12 sm:pt-16">
      <div className="mx-auto max-w-column">
        <Link to={me ? '/' : '/login'} className="mb-14 block w-fit" aria-label={`Never Stop Building, build portal: ${me ? 'back to the portal' : 'sign in'}`}>
          <BrandLockup />
        </Link>
        <h1 className="text-[2.5rem] leading-tight">{title}</h1>
        <p className="mt-3 max-w-2xl text-[1.0625rem] text-ink-2">{lede}</p>
        {children}
        <PublicLinks className="mt-16" />
      </div>
    </div>
  );
}

/** The way round, at the foot of each public page and under the sign-in form. */
export function PublicLinks({ className = '' }: { className?: string }) {
  const { pathname } = useLocation();
  const { data: me } = useMe();
  return (
    <nav aria-label="About the portal" className={`flex flex-wrap gap-x-6 gap-y-2 border-t border-rule pt-5 text-sm ${className}`}>
      {wayRound(pathname, me ? 'signed in' : 'signed out').map(([to, words]) => (
        <Link key={to} to={to} className="link">
          {words}
        </Link>
      ))}
    </nav>
  );
}

function Part({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mt-10 border-t border-rule pt-6">
      <h2 className="text-[1.5rem] leading-tight">{title}</h2>
      <div className="mt-3 space-y-4 text-[1.0625rem] leading-relaxed">{children}</div>
    </section>
  );
}

function Items({ children }: { children: ReactNode }) {
  return <ul className="list-disc space-y-3 pl-5 marker:text-ink-3">{children}</ul>;
}

const Mail = () => (
  <a className="link" href={`mailto:${CONTACT}`}>
    {CONTACT}
  </a>
);

const Effective = ({ on }: { on: string }) => <p className="label mt-6">In effect from {on}</p>;

/** The portal's home page for anyone not signed in: what it is, who it is for, and what it does with Google data. */
export function About() {
  return (
    <PublicPage
      title="NSB Build Portal"
      lede="Where Never Stop Building runs a design and build project with the people it is for: the price and how sure it is, the decisions, the steps of the work, the files, and the record of what happened."
    >
      <Part title="What it is for">
        <p>
          Building goes wrong when nobody wrote down who owns a step, what it includes, when it is needed and what it costs. The portal writes those down before the work is
          due, so a problem is a small fact early instead of a fight late. NSB holds its own work to the same record as everyone else’s.
        </p>
      </Part>
      <Part title="Who uses it">
        <p>
          NSB’s own people, and the clients and trades NSB invites to a project. There is no public sign-up: NSB adds you to a project and sends you an invitation. Each person
          sees only the projects they are on, and a client sees what NSB shares with them.
        </p>
      </Part>
      <Part title="What it does">
        <Items>
          <li>The price of the job in one bar: what is billed and paid, what is priced, what is an allowance and what is held back for the unknown.</li>
          <li>How the price narrows as the design firms up, phase by phase.</li>
          <li>The process: each step of the job, who does it, and where it stands.</li>
          <li>Design reviews: marks and comments on drawings, by everyone asked to review them.</li>
          <li>A design board, the project’s files, and a timeline of what happened.</li>
          <li>The patterns the house should follow, from A Pattern Language, and how much each matters, chosen by NSB and the client together.</li>
        </Items>
      </Part>
      <Part title="Google data">
        <p>
          NSB connects its own Gmail account to the portal. The portal reads it, and never changes it, to keep each project’s email with the project and to take in new enquiries;
          the portal’s own messages, such as sign-in links and invitations, go out as NSB’s address through Resend, and through that Gmail account only when Resend is not
          connected. Nobody else connects a Google account. How that data is handled is in the{' '}
          <Link to="/privacy" className="link">
            privacy policy
          </Link>
          , and the rules for using the portal are in the{' '}
          <Link to="/terms" className="link">
            terms
          </Link>
          .
        </p>
        <p>
          Never Stop Building is at{' '}
          <a className="link" href="https://www.neverstopbuilding.com">
            neverstopbuilding.com
          </a>
          .
        </p>
      </Part>
    </PublicPage>
  );
}

export function Privacy() {
  return (
    <PublicPage title="Privacy" lede="What the NSB Build Portal keeps, why, who else handles it, and what you can ask for.">
      <Effective on={PRIVACY_EFFECTIVE} />
      <Part title="Who we are">
        <p>
          The portal is run by Never Stop Building (NSB), a design and build practice, for its own projects. “We” on this page is NSB. Questions about anything here: <Mail />.
        </p>
      </Part>
      <Part title="What the portal keeps">
        <Items>
          <li>
            <span className="font-medium">Your account.</span> Your name, your email address, how often you want to be emailed and, if you choose one, your password, kept only as a one-way hash. When you sign
            in, the time, your browser’s description of itself and your network address are recorded with the session, so it can be checked and ended.
          </li>
          <li>
            <span className="font-medium">The projects you are on.</span> What NSB and the people on a project add to it: its details and site, the people on it and how to reach
            them, budgets, bills and payments, decisions, the steps of the work, files and pictures, marks and comments on drawings, what is on its design board, and the
            patterns chosen for the house, how much each matters and the notes on why.
          </li>
          <li>
            <span className="font-medium">A record of what was done.</span> Who did what, and when. What was approved is kept as it was, and neither it nor this record is
            edited after the fact; that is the point of the portal.
          </li>
          <li>
            <span className="font-medium">Reports you file</span> with the portal’s feedback button, with a picture of the screen if you add one.
          </li>
        </Items>
      </Part>
      <Part title="Email and Google data">
        <p>The portal connects to one Google account: NSB’s own Gmail. No client, trade or anyone else gives the portal access to their Google account.</p>
        <Items>
          <li>
            <span className="font-medium">Reading.</span> The portal looks in NSB’s mailbox for messages to and from the people on each project and suggests those threads to
            NSB; the threads NSB confirms are kept with the project, with their attachments. It reads mail sent or copied to NSB’s enquiry address and makes a clear new
            enquiry a lead, or holds it for NSB to review; the conversation an inquiry to NSB’s enquiry address came in is kept with the lead it becomes, with its
            attachments. It reads which addresses the account sends as. It never changes, deletes, moves or labels anything in the mailbox.
          </li>
          <li>
            <span className="font-medium">Sending.</span> The portal sends its own messages as the address NSB chooses from those the account sends as: sign-in links, invitations, password resets, emails to clients when something on their project waits on them (as often as each chooses on their Account page, and again when NSB reminds them of one), and notices to NSB. They go out through Resend (below); through the Gmail account itself only when Resend is not connected.
          </li>
          <li>Project email is seen only by NSB. It is never shown to a client.</li>
        </Items>
        <p>
          NSB Build Portal’s use and transfer to any other app of information received from Google APIs will adhere to the{' '}
          <a className="link" href={GOOGLE_POLICY}>
            Google API Services User Data Policy
          </a>
          , including the Limited Use requirements. Google data is used only to provide the features above. It is never sold, never used for advertising, and never used to train
          AI models.
        </p>
      </Part>
      <Part title="Who else handles it">
        <p>The portal uses a few services to do its work. Each handles what it is given to do that work for NSB.</p>
        <Items>
          <li>
            <span className="font-medium">Netlify</span> serves the portal’s pages. <span className="font-medium">Railway</span> runs its server and database and keeps its
            files and backups.
          </li>
          <li>
            <span className="font-medium">Google</span>: Gmail, as above. <span className="font-medium">Google Places</span> suggests addresses: what NSB types into an
            address field is sent to it, and the place picked is asked for and kept with the site, with its coordinates and Google’s id for it. NSB’s own people can open a
            site on Google Maps from the portal. Never a client’s. The pages load their typefaces from Google Fonts.
          </li>
          <li>
            <span className="font-medium">Anthropic (Claude)</span> reads an enquiry sent to NSB’s enquiry address to propose a lead, sums up the comments of a finished design
            review for NSB, researches the town a project is in, and writes the development blog’s article for each release from the developers’ notes on what changed, with every project’s and client’s name taken out. For these, street addresses, email addresses and phone numbers are taken out before anything is sent, and of a site
            only its town and state leave the portal. Anthropic does not train its models on what is sent through its API. Someone at NSB can connect their own Claude account to
            the portal; Claude then reads what that person can see in it, under that account’s terms.
          </li>
          <li>
            <span className="font-medium">OpenAI</span> paints the portal’s pictures, landscapes and seasons, from a project’s name, summary and town; never from a street
            address or a person. It also paints a small picture for each pattern on the Patterns page, from that pattern’s subject alone (a few words on what its picture
            shows), never from anything of a client’s.
          </li>
          <li>
            <span className="font-medium">Resend</span> sends the portal’s own emails: for each, the name and email address it goes to and the email’s words, a sign-in link
            in it included, so it can deliver it. Its open and click tracking is off.
          </li>
          <li>
            <span className="font-medium">Photon</span>, an address lookup run by komoot, suggests them instead while Google Places is not connected: what is typed into an
            address field is then sent to it.
          </li>
          <li>
            <span className="font-medium">GitHub</span>: reports filed with the feedback button go to NSB’s private software repository there.
          </li>
        </Items>
      </Part>
      <Part title="Who sees what">
        <p>
          NSB’s people see the projects they work on. A client sees the projects they are on, and only what NSB shares with them there. What NSB keeps for itself, such as its
          costs, its notes and project email, stays NSB’s.
        </p>
      </Part>
      <Part title="Cookies">
        <p>
          One cookie keeps you signed in. Your browser also keeps a few settings, such as the last view you chose, and a copy of the portal’s pages so it opens quickly. There
          are no advertising or analytics trackers.
        </p>
      </Part>
      <Part title="How long it is kept">
        <p>
          A project’s record is kept while the project is: what was agreed, approved, paid and done is what the portal is for, and nothing approved is changed. If NSB
          deletes a project, its record and files are deleted with it and cannot be brought back in the portal. Nightly backups are kept for 30 days. You can ask NSB to close your account and take you off a project; your access ends at once, and what you did on the project stays in its record under your
          name.
        </p>
      </Part>
      <Part title="What you can ask for">
        <p>
          To see what the portal holds about you, to have it corrected, or to have your account closed, write to <Mail />.
        </p>
      </Part>
      <Part title="Keeping it safe">
        <p>
          Everything travels encrypted. Passwords are kept only as one-way hashes, and the keys to connected accounts are stored encrypted. Every request is checked against the
          projects the person asking may see.
        </p>
      </Part>
      <Part title="Changes">
        <p>If this policy changes, the new version is posted here with the date it takes effect.</p>
      </Part>
    </PublicPage>
  );
}

export function Terms() {
  return (
    <PublicPage
      title="Terms of use"
      lede="The rules for using the NSB Build Portal. Your agreement with NSB for the work itself is a separate document, and where the two differ, that agreement governs."
    >
      <Effective on={TERMS_EFFECTIVE} />
      <Part title="The portal">
        <p>Never Stop Building (NSB) runs the portal for its own projects. NSB invites the people who use it: its own people, its clients, and the trades working on a project.</p>
      </Part>
      <Part title="Your account">
        <p>Keep your sign-in to yourself, and tell NSB at once if you think someone else has used it. What is done under your account is recorded as yours.</p>
      </Part>
      <Part title="What you add">
        <p>
          Files, pictures, comments and marks you add stay yours. You let NSB keep them with the project and show them to the people on it, as the portal does. Add only what you
          have the right to share.
        </p>
      </Part>
      <Part title="Prices, dates and approvals">
        <p>
          Amounts and dates in the portal describe the project as it stands, with how sure they are, and can change until they are agreed. The price and terms that bind are in
          your agreement with NSB and the changes approved under it. When you approve or decide something in the portal, it is recorded with your name and the
          time, and NSB relies on it as your answer.
        </p>
      </Part>
      <Part title="The record">
        <p>The portal keeps a permanent record of each project. What was agreed or approved is never edited; a correction is a new entry beside it.</p>
      </Part>
      <Part title="Using it fairly">
        <p>Use the portal only for the projects you are on. Do not try to reach what is not shared with you, get round its security, or disrupt it.</p>
      </Part>
      <Part title="While it is being built">
        <p>
          The portal is under active development. It changes, and it may sometimes be unavailable or wrong. If something looks wrong, report it with the feedback button or tell
          NSB, and keep your own copy of anything you cannot do without.
        </p>
      </Part>
      <Part title="Ending access">
        <p>NSB may end someone’s access when their part in a project ends, or if these terms are broken. The project’s record stays.</p>
      </Part>
      <Part title="Responsibility">
        <p>
          The portal is provided as it is. As far as the law allows, NSB is not responsible for losses that come from using it, beyond what your agreement with NSB provides.
        </p>
      </Part>
      <Part title="Changes and questions">
        <p>
          If these terms change, the new version is posted here with the date it takes effect. Questions: <Mail />.
        </p>
      </Part>
    </PublicPage>
  );
}
